import { Divider, Box, CircularProgress } from "@mui/material";
import { createUseStyles } from "react-jss";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { theme } from "$utils/theme/bataillog";

import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";
import { MESSAGES } from "$components/EDB/IntegratedOrders";
import {
  ORDERS_FLEX_WIDTH,
  RECEPTIONS_FLEX_WIDTH,
} from "$containers/EDB/OrdersAndReceptions/constants";
import { Text } from "$components";
import JOrderTree from "$components/JOrderTree/JOrderTree";
import JReceptionTree from "$components/JReceptionTree/JReceptionTree";
import { TreeReference } from "$components/JReceptionTree/useReceptionForTree";
import { Order, Reception } from "$types";
import ReferenceType from "$types/ReferenceType";
import Header from "./Header";
import useAvailableReceptions from "./useAvailableReceptions";
import { edbIoToOrders } from "../IntegratedOrdersPopin/utils";
import useRemainingIntegratedOrders from "./useRemainingIntegratedOrders";

type Props = {
  edb: any;
  fetchEdb: () => void;
  fetchIntegratedOrders: (id: number | string, forceRefresh?: boolean) => void;
  integratedOrders: EDBIntegratedOrders;
  isDisabled: boolean;
};

function OrdersAndReceptions({
  edb,
  fetchEdb,
  fetchIntegratedOrders,
  integratedOrders,
  isDisabled,
}: Props) {
  const classes = useStyles();
  const [selectedItems, setSelectedItems] = useState<TreeReference[]>([]);
  const noReceptionsFoundMessage = useRef("Aucune réception trouvée");

  const handleOnCheck = (
    checkedItems: TreeReference[],
    receptions: Reception[]
  ) => {
    const receptionsIds = receptions.map((r) => r.id);
    const otherItems = selectedItems.filter(
      (item) => !receptionsIds.includes(item.reception?.id)
    );
    setSelectedItems([...checkedItems, ...otherItems]);
  };

  const { remainingIntegratedOrders } = useRemainingIntegratedOrders(
    integratedOrders,
    edb?.products || []
  );

  const orders = useMemo(
    () =>
      edbIoToOrders(remainingIntegratedOrders).map((order) => ({
        ...order,
        hasOnlyOneSuborder: order.suborders.length === 1,
      })),
    [remainingIntegratedOrders]
  );

  const orderIds = useMemo(() => orders.map((order) => order.id), [orders]);

  const {
    fetchReceptions,
    isFetching: isFetchingReceptions,
    getOrderReceptions,
  } = useAvailableReceptions(remainingIntegratedOrders, orderIds);

  const getRelatedIntegratedOrders = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (order: Order) => ({
      ...remainingIntegratedOrders,
      orderProducts: remainingIntegratedOrders.orderProducts,
    }),
    [remainingIntegratedOrders]
  );

  const emptySelectedItems = () => setSelectedItems([]);

  const handleAfterReferencing = useCallback(async () => {
    await fetchReceptions();
    if (edb?._id) {
      await fetchIntegratedOrders(edb?._id, true);
    }
    await fetchEdb();
  }, [fetchReceptions, fetchIntegratedOrders, fetchEdb, edb?._id]);

  const isLoading = isFetchingReceptions;
  const hasInitialIntegratedOrders = !!integratedOrders;

  // Check if all integrated order products have been treated
  // Products from orders are ONLY added to edb.products when treated
  // But edb.products can also contain manually-added products.
  // The reliable discriminator for "treated integrated-order product" is `receptionReferenceId`
  // (it's present in the payload when a product is treated from an order).
  const allProductsTreated = useMemo(() => {
    const integratedCount = integratedOrders?.orderProducts?.length ?? 0;
    const remainingCount =
      remainingIntegratedOrders?.orderProducts?.length ?? 0;

    if (integratedCount === 0) return false;
    return remainingCount === 0;
  }, [
    integratedOrders?.orderProducts?.length,
    remainingIntegratedOrders?.orderProducts?.length,
  ]);

  const hasACommandIntegrated =
    (remainingIntegratedOrders?.orderProducts?.length ?? 0) > 0;

  const processedReceptionIds = new Set();
  return (
    <Box flex={1} width="100%">
      <Header
        edb={edb}
        emptySelectedItems={emptySelectedItems}
        fetchEdb={fetchEdb}
        fetchIntegratedOrders={fetchIntegratedOrders}
        isACommandIntegrated={hasACommandIntegrated}
        isDisabled={isDisabled}
        selectedItems={selectedItems}
        orders={orders}
      />
      {hasACommandIntegrated ? (
        <div className={classes.ordersAndReceptions}>
          {orders.map((order, i) => {
            const allReceptions = getOrderReceptions(order.id);
            const receptions = allReceptions.filter((reception) => {
              if (
                processedReceptionIds.has(reception.id) &&
                reception.receptionReferences &&
                reception.receptionReferences.length > 0 &&
                reception.receptionReferences[0].referenceType ===
                  ReferenceType.ORDER
              ) {
                noReceptionsFoundMessage.current = "";
                return false;
              }
              processedReceptionIds.add(reception.id);
              return true;
            });

            return (
              <Fragment key={order.id}>
                <Box display="flex">
                  <Box flex={ORDERS_FLEX_WIDTH} overflow="hidden">
                    {hasACommandIntegrated ? (
                      <JOrderTree
                        expandAllNodes
                        orders={[order]}
                        key={order.id}
                        hideProductsWithQtyZero
                      />
                    ) : hasInitialIntegratedOrders ? (
                      <Text>{MESSAGES.NO_INTEGRATED_PRODUCTS}</Text>
                    ) : (
                      <Text>{MESSAGES.ALL_PRODUCTS_TREATED}</Text>
                    )}
                  </Box>
                  <Divider
                    orientation="vertical"
                    flexItem
                    className={classes.verticalDivider}
                  />
                  <Box
                    display="flex"
                    justifyContent="center"
                    flex={RECEPTIONS_FLEX_WIDTH}
                    overflow="hidden"
                  >
                    {isLoading ? (
                      <Box height="100%" display="flex">
                        <CircularProgress />
                      </Box>
                    ) : (
                      <JReceptionTree
                        key={`reception-tree-${order.id}-${receptions.length}`}
                        expandAllNodes
                        integratedOrders={getRelatedIntegratedOrders(order)}
                        edbProducts={edb?.products}
                        onCheck={(items) => handleOnCheck(items, receptions)}
                        onEdit={handleAfterReferencing}
                        receptions={receptions}
                        showCheckbox
                        showCheckboxForProductQuantity={false}
                        noReceptionsFoundMessage={
                          noReceptionsFoundMessage.current
                        }
                      />
                    )}
                  </Box>
                </Box>
                {i !== orders.length - 1 && <Divider />}
              </Fragment>
            );
          })}
        </div>
      ) : (
        <Text>
          {allProductsTreated && hasInitialIntegratedOrders
            ? MESSAGES.ALL_PRODUCTS_TREATED
            : MESSAGES.NO_INTEGRATED_PRODUCTS}
        </Text>
      )}
    </Box>
  );
}

export default OrdersAndReceptions;

const useStyles = createUseStyles(() => ({
  bold: {
    fontWeight: "bold",
  },
  ordersAndReceptions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  verticalDivider: {
    margin: "0 15px 0 15px !important",
  },
}));
